
import React, { useEffect } from "react";
import { formatCurrency } from "@/lib/calculations";
import { MATT_MEDIA_FEE_PERCENTAGE } from "@/lib/constants";

interface ResultCardProps {
  minEarnings: number;
  maxEarnings: number;
  periodLabel: string;
  feeAmount: {
    min: number;
    max: number;
  };
  emailFeeAmount?: {
    min: number;
    max: number;
  };
  totalEarnings: {
    min: number;
    max: number;
  };
  projections?: {
    day: { min: number; max: number };
    week: { min: number; max: number };
    month: { min: number; max: number };
    quarter: { min: number; max: number };
    year: { min: number; max: number };
  };
}

const ResultCard: React.FC<ResultCardProps> = ({
  minEarnings,
  maxEarnings,
  periodLabel,
  feeAmount,
  emailFeeAmount,
  totalEarnings,
  projections
}) => {
  useEffect(() => {
    console.log("ResultCard rendered with data:", { 
      minEarnings, 
      maxEarnings, 
      periodLabel,
      feeAmount,
      emailFeeAmount,
      totalEarnings,
      projections
    });
    
    // Enhanced domain debugging
    console.log("Domain Debugging Information:");
    console.log("Current hostname:", window.location.hostname);
    console.log("Full URL:", window.location.href);
    console.log("Protocol:", window.location.protocol);
    console.log("Path:", window.location.pathname);
    console.log("Search params:", window.location.search);
    console.log("Hash:", window.location.hash);
    console.log("Document.referrer:", document.referrer);
    
    // Check for caching headers if possible
    try {
      const performanceEntries = performance.getEntriesByType("navigation");
      console.log("Performance navigation data:", performanceEntries);
    } catch (e) {
      console.log("Performance API not available:", e);
    }
    
    // Add visible debugging info to the page (will be removed in production)
    if (process.env.NODE_ENV !== 'production') {
      const debugDiv = document.createElement('div');
      debugDiv.id = 'domain-debug-info';
      debugDiv.style.position = 'fixed';
      debugDiv.style.bottom = '10px';
      debugDiv.style.right = '10px';
      debugDiv.style.backgroundColor = 'rgba(0,0,0,0.7)';
      debugDiv.style.color = 'white';
      debugDiv.style.padding = '10px';
      debugDiv.style.borderRadius = '5px';
      debugDiv.style.fontSize = '12px';
      debugDiv.style.zIndex = '9999';
      debugDiv.style.maxWidth = '300px';
      debugDiv.innerHTML = `
        <p><strong>Domain:</strong> ${window.location.hostname}</p>
        <p><strong>URL:</strong> ${window.location.href}</p>
        <p><strong>Build time:</strong> ${new Date().toISOString()}</p>
        <button id="clear-cache-btn" style="background: #f44336; color: white; border: none; padding: 5px 10px; border-radius: 3px; cursor: pointer; margin-top: 5px;">Clear Local Cache</button>
      `;
      
      // Append to body if it doesn't exist yet
      if (!document.getElementById('domain-debug-info')) {
        document.body.appendChild(debugDiv);
        
        // Add event listener for the clear cache button
        setTimeout(() => {
          const clearBtn = document.getElementById('clear-cache-btn');
          if (clearBtn) {
            clearBtn.addEventListener('click', () => {
              try {
                localStorage.clear();
                sessionStorage.clear();
                const allCookies = document.cookie.split(';');
                for (let i = 0; i < allCookies.length; i++) {
                  const cookie = allCookies[i];
                  const eqPos = cookie.indexOf("=");
                  const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
                  document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
                }
                alert('Local cache cleared! Refreshing page...');
                window.location.reload();
              } catch (e) {
                alert('Failed to clear cache: ' + e.message);
              }
            });
          }
        }, 500);
      }
    }
  }, [minEarnings, maxEarnings, periodLabel, feeAmount, emailFeeAmount, totalEarnings, projections]);

  return (
    <div className="result-card w-full max-w-md mx-auto bg-white rounded-lg p-6 shadow-lg border border-gray-200">
      <h3 className="text-xl font-semibold mb-4">Your Newsletter Potential</h3>
      
      <div className="space-y-6">
        <div>
          <p className="text-sm text-muted-foreground mb-1">Approx. Gross Revenue Per {periodLabel}</p>
          <p className="text-3xl font-bold">
            {formatCurrency(minEarnings)} - {formatCurrency(maxEarnings)}
          </p>
        </div>
      </div>
      
      <div className="mt-8">
        <p className="text-center text-sm text-muted-foreground">
          Working with Vacant Media could generate you an extra{" "}
          <span className="font-semibold">
            {formatCurrency(minEarnings)} - {formatCurrency(maxEarnings)}
          </span>{" "}
          per {periodLabel}.
        </p>
      </div>
    </div>
  );
};

export default ResultCard;
